import { supabase } from '@/lib/supabase';
import {
  Purchase,
  PurchasePayment,
  PaginatedResult,
} from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchPurchasesParams {
  search?: string;
  supplierId?: string;
  status?: string;
  archiveFilter?: 'active' | 'archived' | 'all';
  page?: number;
}

export async function fetchPurchases(
  params: FetchPurchasesParams
): Promise<PaginatedResult<Purchase>> {
  const {
    search = '',
    supplierId = '',
    status = '',
    archiveFilter = 'active',
    page = 0,
  } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('purchases')
    .select('*, supplier:suppliers(id, name)', { count: 'exact' })
    .order('purchase_date', { ascending: false })
    .order('created_at', { ascending: false })
    .order('purchase_number', { ascending: false })
    .range(from, to);

  if (archiveFilter === 'active') {
    query = query.is('archived_at', null);
  } else if (archiveFilter === 'archived') {
    query = query.not('archived_at', 'is', null);
  }

  if (search.trim()) {
    const s = search.trim();
    // schema columns: supplier_invoice_number, notes
    query = query.or(`supplier_invoice_number.ilike.%${s}%,notes.ilike.%${s}%`);
  }

  if (supplierId) query = query.eq('supplier_id', supplierId);
  if (status) query = query.eq('payment_status', status);

  const { data, error, count } = await query;
  if (error) throw error;

  return { data: (data ?? []) as Purchase[], count: count ?? 0 };
}

export async function fetchPurchaseById(id: string): Promise<Purchase> {
  const { data, error } = await supabase
    .from('purchases')
    .select('*, supplier:suppliers(id, name), items:purchase_items(*, laptop:laptops(*), accessory:accessories(*))')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Purchase;
}

export async function fetchPurchasePayments(purchaseId: string): Promise<PurchasePayment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('purchase_id', purchaseId)
    .eq('direction', 'out')
    .order('payment_date', { ascending: false });

  if (error) return [];
  return (data ?? []) as PurchasePayment[];
}

/**
 * receive_purchase RPC — exact parameter names from 01_SUPABASE_SCHEMA_V2.sql
 * The RPC item JSON keys that the function body reads:
 *   type, stock_id, serial_number, service_tag, barcode, brand, model,
 *   cpu, ram, storage, gpu, screen_size, resolution, colour, keyboard_layout,
 *   operating_system, condition, grade, battery_health, charger_included,
 *   charger_notes, accessories_included, refurbishment_cost, other_cost,
 *   selling_price (→ written to laptops.asking_price), minimum_price,
 *   warranty_months, description, notes, is_public,
 *   unit_cost, quantity,
 *   accessory_id, sku, name, category, brand, reorder_level
 *
 * NOTE: laptop JSON uses `selling_price` key which the RPC writes into
 * laptops.asking_price — this is intentional per the SQL function body.
 */
export interface ReceivePurchasePayload {
  supplier_id: string;
  reference?: string | null;          // p_reference
  purchase_date?: string;
  shipping_cost?: number;
  other_cost?: number;               // p_other_cost (singular)
  discount?: number;                 // p_discount
  notes?: string | null;
  amount_paid?: number;
  payment_method?: string;
  items: Array<
    | {
        type: 'laptop';
        stock_id?: string;
        serial_number?: string | null;
        service_tag?: string | null;
        barcode?: string | null;
        brand: string;
        model: string;
        cpu?: string | null;
        ram?: string | null;
        storage?: string | null;
        gpu?: string | null;
        screen_size?: string | null;
        touchscreen?: boolean | null;
        resolution?: string | null;
        colour?: string | null;
        keyboard_layout?: string | null;
        operating_system?: string | null;
        condition?: string;
        grade?: string | null;
        battery_health?: number | null;
        charger_included?: boolean;
        charger_notes?: string | null;
        accessories_included?: string | null;
        refurbishment_cost?: number;
        other_cost?: number;
        selling_price?: number | null;   // RPC writes this to laptops.asking_price
        minimum_price?: number | null;
        warranty_months?: number;
        description?: string | null;
        notes?: string | null;
        is_public?: boolean;
        unit_cost: number;
      }
    | {
        type: 'accessory';
        accessory_id?: string;
        sku?: string | null;
        barcode?: string | null;
        name?: string;
        category?: string | null;
        brand?: string | null;
        quantity: number;
        unit_cost: number;
        selling_price?: number;
        reorder_level?: number;
        description?: string | null;
        is_public?: boolean;
      }
  >;
}

export async function receivePurchaseRpc(payload: ReceivePurchasePayload): Promise<void> {
  const { error } = await supabase.rpc('receive_purchase', {
    p_supplier_id: payload.supplier_id,
    p_reference: payload.reference ?? null,
    p_purchase_date: payload.purchase_date ?? null,
    p_shipping_cost: payload.shipping_cost ?? 0,
    p_other_cost: payload.other_cost ?? 0,
    p_discount: payload.discount ?? 0,
    p_notes: payload.notes ?? null,
    p_amount_paid: payload.amount_paid ?? 0,
    p_payment_method: payload.payment_method ?? 'cash',
    p_items: payload.items,
  });

  if (error) throw error;
}

/**
 * record_purchase_payment — exact parameter names from SQL:
 * p_purchase_id, p_amount, p_payment_method, p_reference, p_notes
 */
export async function recordPurchasePaymentRpc(params: {
  purchase_id: string;
  amount: number;
  payment_method: string;
  reference?: string;
  notes?: string;
}): Promise<void> {
  const { error } = await supabase.rpc('record_purchase_payment', {
    p_purchase_id: params.purchase_id,
    p_amount: params.amount,
    p_payment_method: params.payment_method,
    p_reference: params.reference ?? null,
    p_notes: params.notes ?? null,
  });

  if (error) throw error;
}

export async function voidPurchaseRpc(params: {
  purchaseId: string;
  reason: string;
  reversePayment?: boolean;
}): Promise<{
  success: boolean;
  purchase_id: string;
  purchase_number: string;
  status: string;
  laptops_removed: number;
  accessories_deducted: number;
  reversed_payment: boolean;
  amount_reversed: number;
  message: string;
}> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required to cancel / void a purchase.');
  }

  const { data, error } = await supabase.rpc('void_purchase', {
    p_purchase_id: params.purchaseId,
    p_reason: params.reason.trim(),
    p_reverse_payment: params.reversePayment ?? false,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    purchase_id: string;
    purchase_number: string;
    status: string;
    laptops_removed: number;
    accessories_deducted: number;
    reversed_payment: boolean;
    amount_reversed: number;
    message: string;
  };
}

export async function archivePurchaseRpc(params: {
  purchaseId: string;
  reason: string;
}): Promise<{
  success: boolean;
  purchase_id: string;
  purchase_number: string;
  status: string;
  message: string;
}> {
  if (!params.reason.trim()) {
    throw new Error('A reason is required to archive a purchase.');
  }

  const { data, error } = await supabase.rpc('archive_purchase', {
    p_purchase_id: params.purchaseId,
    p_reason: params.reason.trim(),
  });

  if (error) throw error;
  return data as {
    success: boolean;
    purchase_id: string;
    purchase_number: string;
    status: string;
    message: string;
  };
}

export async function correctPurchaseInvoiceRpc(params: {
  purchase_id: string;
  reason: string;
  supplier_invoice_number?: string | null;
  purchase_date?: string | null;
  notes?: string | null;
  supplier_id?: string | null;
  laptop_corrections?: Array<{
    laptop_id: string;
    brand?: string;
    model?: string;
    serial_number?: string | null;
    service_tag?: string | null;
    barcode?: string | null;
    cpu?: string | null;
    ram?: string | null;
    storage?: string | null;
    gpu?: string | null;
    screen_size?: string | null;
    touchscreen?: boolean | null;
    colour?: string | null;
    condition?: string;
    grade?: string | null;
    battery_health?: number | null;
    charger_included?: boolean;
    charger_notes?: string | null;
    warranty_months?: number;
    purchase_price?: number;
    allocated_cost?: number;
    asking_price?: number | null;
    minimum_price?: number | null;
    notes?: string | null;
  }>;
  accessory_corrections?: Array<{
    purchase_item_id: string;
    quantity: number;
    unit_cost: number;
  }>;
  discount?: number;
}): Promise<{
  success: boolean;
  purchase_id: string;
  purchase_number: string;
  new_total?: number;
  new_balance?: number;
  payment_status?: string;
}> {
  if (!params.reason?.trim()) {
    throw new Error('A reason is required to correct a purchase.');
  }

  const { data, error } = await supabase.rpc('correct_purchase_invoice', {
    p_purchase_id: params.purchase_id,
    p_reason: params.reason.trim(),
    p_supplier_invoice_number: params.supplier_invoice_number ?? null,
    p_purchase_date: params.purchase_date ?? null,
    p_notes: params.notes ?? null,
    p_supplier_id: params.supplier_id ?? null,
    p_laptop_corrections: params.laptop_corrections ?? [],
    p_accessory_corrections: params.accessory_corrections ?? [],
    p_discount: params.discount !== undefined ? params.discount : null,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    purchase_id: string;
    purchase_number: string;
    new_total?: number;
    new_balance?: number;
    payment_status?: string;
  };
}

export async function unarchivePurchaseRpc(purchaseId: string): Promise<{
  success: boolean;
  purchase_id: string;
  purchase_number: string;
  status: string;
  message: string;
}> {
  const { data, error } = await supabase.rpc('unarchive_purchase', {
    p_purchase_id: purchaseId,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    purchase_id: string;
    purchase_number: string;
    status: string;
    message: string;
  };
}


